import { Flex } from '@chakra-ui/react';
import { NAVBAR_HEIGHT } from 'constants/editor';
import { ReactElement, useEffect } from 'react';
import { Provider } from 'react-redux';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import { WidgetRenderer } from 'widgets/WidgetRenderer';

import { ChameleonProvider } from '../../libs/third-party/Chameleon';
import { ZendeskProvider } from '../../libs/third-party/Zendesk/zendeskProvider';
import { E2ClientProvider } from '../E2ClientProvider';
import { InfographLoader } from '../InfographLoader';
import { FocusProvider } from './components/Focus';
import { Head } from './components/Head';
import { KeyboardShortcut } from './components/KeyboardShortcut';
import { Navbar } from './components/Navbar';
import { PageContainer } from './components/PageArea/PageContainer';
import { PageScrollContainer } from './components/PageArea/PageScrollContainer';
import { PageTransformContainer } from './components/PageArea/PageTransformContainer';
import { PageControls } from './components/PageControls/PageControls';
import { Sidebar } from './components/WidgetMenu/Sidebar';
import { store, useAppSelector } from './store';
import { setViewOnly } from './store/editorSettingsSlice';
import { selectActivePage } from './store/pageSelector';

const ViewOnlyEditor = (): ReactElement => {
  // set view only is true and ensure the dispatch only run once
  useEffect(() => {
    const { isViewOnly } = store.getState().editorSettings;
    if (!isViewOnly) store.dispatch(setViewOnly(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ZendeskProvider>
      <ChameleonProvider>
        <Provider store={store}>
          <Head />
          <E2ClientProvider>
            <FocusProvider>
              <Navbar />
              <InfographLoader>
                <Flex as='main' minH={`calc(100vh - ${NAVBAR_HEIGHT}px)`}>
                  <Sidebar readonly />
                  <Flex direction={'column'} position={'relative'}>
                    <PageControls />
                    <ViewOnlyContent />
                  </Flex>
                </Flex>
                {/* Added condition only remain enter */}
                <KeyboardShortcut />
              </InfographLoader>
            </FocusProvider>
          </E2ClientProvider>
        </Provider>
      </ChameleonProvider>
    </ZendeskProvider>
  );
};

export default ViewOnlyEditor;

const ViewOnlyContent = () => {
  const activePageId = useAppSelector(selectActivePage);

  return (
    <PageScrollContainer>
      <PageTransformContainer>
        <PageContainer>
          <WidgetRenderer pageId={activePageId} isReadOnly renderType={ReadOnlyRenderType.ThumbnailReadOnly} />
        </PageContainer>
      </PageTransformContainer>
    </PageScrollContainer>
  );
};
